import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

type PageState = 'form' | 'submitting' | 'success' | 'invalid';

export default function ResetPassword() {
  const [searchParams]  = useSearchParams();
  const token           = searchParams.get('token') ?? '';
  const navigate        = useNavigate();

  const [pageState,        setPageState]        = useState<PageState>(token ? 'form' : 'invalid');
  const [password,         setPassword]         = useState('');
  const [confirmPassword,  setConfirmPassword]  = useState('');
  const [showPassword,     setShowPassword]     = useState(false);
  const [error,            setError]            = useState('');

  useEffect(() => {
    if (!token) setPageState('invalid');
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setPageState('submitting');
    try {
      await api.post('/api/auth/reset-password', { token, newPassword: password });
      setPageState('success');
    } catch (err: any) {
      if (err.message?.toLowerCase().includes('invalid') || err.message?.toLowerCase().includes('expired')) {
        setPageState('invalid');
      } else {
        setError(err.message || 'Failed to reset password. Please try again.');
        setPageState('form');
      }
    }
  };

  if (pageState === 'invalid') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#fffbeb' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h1 className="auth-title">Link Expired</h1>
          <p className="auth-subtitle">
            This password reset link is invalid or has expired.<br />
            Please request a new one from the login page.
          </p>
          <button
            className="btn btn-primary btn-full"
            style={{ marginTop: 24 }}
            onClick={() => navigate('/login')}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ background: '#f0fdf7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#1C9B82" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h1 className="auth-title">Password Set!</h1>
          <p className="auth-subtitle">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <button
            className="btn btn-primary btn-full"
            style={{ marginTop: 24 }}
            onClick={() => navigate('/login')}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>
        <h1 className="auth-title">Set New Password</h1>
        <p className="auth-subtitle">Choose a strong password for your account.</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label" htmlFor="password">New Password</label>
            <div className="input-with-icon">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="At least 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                required
              />
              <button
                type="button"
                className="input-icon-btn"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirm-password">Confirm Password</label>
            <input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              className="form-input"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="auth-error-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={pageState === 'submitting'}
          >
            {pageState === 'submitting' ? <><span className="btn-spinner" /> Saving…</> : 'Set Password'}
          </button>
        </form>

        <button className="auth-link-btn" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
          ← Back to Login
        </button>
      </div>
    </div>
  );
}
